/**
 * Collection Load More Pagination
 * Handles infinite scroll / load more functionality for collection products
 * Products are appended to existing grid instead of replacing content
 * Preserves tab-collection parameter during pagination
 */

class CollectionLoadMore {
  constructor() {
    this.pagination = document.querySelector('[data-pagination-load-more]');
    if (!this.pagination) {
      return;
    }

    this.button = this.pagination.querySelector('button');
    if (!this.button) {
      return;
    }

    this.buttonText = this.button.querySelector('.button__text');
    this.buttonLoading = this.button.querySelector('.button__loading');
    this.productList = document.querySelector('product-list');
    this.collectionContainer = document.querySelector('[data-collection-container]');
    this.isLoading = false;
    this.abortController = null;

    this.init();
  }

  init() {
    if (!this.button) return;

    // Bind the method to ensure correct 'this' context
    this.loadMore = this.loadMore.bind(this);
    
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.loadMore();
    });

    // Listen for tab changes to reset pagination
    window.addEventListener('collection:changed', () => {
      this.resetPagination();
    });

    // Listen for section reloads (after tab switch)
    document.addEventListener('section:loaded', () => {
      this.resetPagination();
    });
  }

  async loadMore() {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoadingState();

    try {
      const nextPageUrl = this.button.dataset.nextPageUrl;
      const collectionHandle = this.button.dataset.collectionHandle;
      
      // Get tab-collection parameter if it exists
      const urlParams = new URLSearchParams(window.location.search);
      const tabCollection = urlParams.get('tab-collection') || 
                           this.collectionContainer?.dataset.activeCollection;

      // Build URL with tab-collection parameter
      const url = new URL(nextPageUrl, window.location.origin);
      if (tabCollection) {
        url.searchParams.set('tab-collection', tabCollection);
      }

      // Abort previous request if exists
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract new products (using custom element selectors)
      const newProducts = doc.querySelectorAll('product-list > product-card');
      
      if (newProducts.length > 0) {
        // Append new products to existing list
        newProducts.forEach(product => {
          this.productList.appendChild(product.cloneNode(true));
        });

        // Update pagination button
        const newPagination = doc.querySelector('[data-pagination-load-more]');
        if (newPagination) {
          const newButton = newPagination.querySelector('button');
          this.button.dataset.nextPageUrl = newButton.dataset.nextPageUrl;
          this.button.dataset.currentPage = newButton.dataset.currentPage;
          
          // Update info text
          const newInfo = newPagination.querySelector('.pagination__info');
          const currentInfo = this.pagination.querySelector('.pagination__info');
          if (newInfo && currentInfo) {
            currentInfo.textContent = newInfo.textContent;
          }
        } else {
          // No more pages - hide button
          this.pagination.style.display = 'none';
        }

        // Dispatch event for any other components that need to know
        document.dispatchEvent(new CustomEvent('collection:productsLoaded', {
          detail: { count: newProducts.length }
        }));
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  showLoadingState() {
    this.button.disabled = true;
    this.buttonText.style.display = 'none';
    this.buttonLoading.style.display = 'inline-block';
  }

  hideLoadingState() {
    this.button.disabled = false;
    this.buttonText.style.display = 'inline-block';
    this.buttonLoading.style.display = 'none';
  }

  showError() {
    const originalText = this.buttonText.textContent;
    this.buttonText.textContent = 'Error loading products. Click to retry.';
    
    setTimeout(() => {
      this.buttonText.textContent = originalText;
    }, 3000);
  }

  resetPagination() {
    // When collection tab changes, we need to wait for the new content
    // to be loaded and then reinitialize
    setTimeout(() => {
      const newPagination = document.querySelector('[data-pagination-load-more]');
      if (newPagination && newPagination !== this.pagination) {
        this.pagination = newPagination;
        this.button = this.pagination.querySelector('button');
        this.buttonText = this.button.querySelector('.button__text');
        this.buttonLoading = this.button.querySelector('.button__loading');
        this.productList = document.querySelector('product-list');
        this.init();
      }
    }, 100);
  }
}

// Global instance
let collectionLoadMoreInstance = null;

// Initialize function
function initCollectionLoadMore() {
  // Destroy previous instance if exists
  if (collectionLoadMoreInstance) {
    collectionLoadMoreInstance = null;
  }
  
  // Create new instance
  const instance = new CollectionLoadMore();
  if (instance.button) {
    collectionLoadMoreInstance = instance;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionLoadMore);
} else {
  // DOM is already ready
  initCollectionLoadMore();
}

// Reinitialize after section updates (for Shopify's Section Rendering API)
document.addEventListener('shopify:section:load', () => {
  setTimeout(initCollectionLoadMore, 100);
});

// Also reinitialize when section is reloaded
document.addEventListener('section:loaded', () => {
  setTimeout(initCollectionLoadMore, 100);
});
